import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchDriversStats,
  createDriver,
  updateDriver,
  deleteDriver,
  bulkDeleteDrivers,
} from "@/lib/api";
import { useSites } from "@/lib/sites";
import { useAuth, filterSitesForUser, getUserAllowedSites } from "@/lib/auth";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { toast } from "sonner";
import {
  ArrowLeft,
  Users,
  MagnifyingGlass,
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  Plus,
  Trash,
  PencilSimple,
  Phone,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR");
  } catch {
    return iso;
  }
};

function DriverDialog({ open, setOpen, editing, onSaved }) {
  const { sites: sitesList } = useSites();
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "", site: "ALL" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.driver || editing.name || "",
        phone: editing.phone || "",
        email: editing.email || "",
        notes: editing.notes || "",
        site: editing.site || "ALL",
      });
    } else {
      setForm({ name: "", phone: "", email: "", notes: "", site: "ALL" });
    }
  }, [editing, open]);

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Nom requis");
      return;
    }
    setSubmitting(true);
    try {
      if (editing && editing.id) {
        await updateDriver(editing.id, form);
        toast.success("Chauffeur mis à jour");
      } else {
        await createDriver(form);
        toast.success("Chauffeur ajouté");
      }
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-none border border-zinc-900 max-w-md">
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-black uppercase tracking-tight"
            style={{ fontFamily: "Chivo" }}
          >
            {editing ? "Modifier chauffeur" : "Nouveau chauffeur"}
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Enregistrez les coordonnées du chauffeur (nom obligatoire).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tag">Nom *</Label>
            <Input
              className="rounded-none border-zinc-900"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jean Dupont"
              data-testid="driver-input-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Téléphone</Label>
              <Input
                className="rounded-none border-zinc-900 mono"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="06 12 34 56 78"
                data-testid="driver-input-phone"
              />
            </div>
            <div>
              <Label className="label-tag">Email</Label>
              <Input
                type="email"
                className="rounded-none border-zinc-900"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jean@flotte.fr"
                data-testid="driver-input-email"
              />
            </div>
          </div>
          <div>
            <Label className="label-tag">Notes</Label>
            <Textarea
              className="rounded-none border-zinc-900 min-h-[60px]"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Permis B, dispo le matin…"
              data-testid="driver-input-notes"
            />
          </div>
          <div>
            <Label className="label-tag">Équipe / Site</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {[
                ...sitesList.map((s) => ({ v: s.code, l: s.name ? `${s.code} · ${s.name}` : s.code })),
                { v: "ALL", l: "Polyvalent" },
              ].map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setForm({ ...form, site: s.v })}
                  className={`flex-1 px-2 py-2 text-[10px] font-bold uppercase tracking-widest border ${
                    form.site === s.v
                      ? "bg-zinc-900 border-zinc-900 text-white"
                      : "bg-white border-zinc-300 hover:border-zinc-900"
                  }`}
                  data-testid={`driver-site-${s.v}`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="rounded-none border-zinc-900"
            data-testid="driver-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-none bg-zinc-900 text-white hover:bg-zinc-700"
            data-testid="driver-save-btn"
          >
            {submitting ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DriverCard({ stat, expanded, onToggle, onEdit, onDelete, selected, onToggleSelect }) {
  const sev = stat.severity_breakdown || { mineur: 0, majeur: 0, critique: 0 };
  return (
    <div
      className={`bg-white border border-zinc-200 hover:border-zinc-900 transition-none ${
        selected ? "ring-2 ring-[var(--ny-gold)] ring-inset" : ""
      }`}
      data-testid={`driver-card-${stat.driver}`}
    >
      <div
        onClick={onToggle}
        className="grid grid-cols-12 gap-3 px-4 py-4 cursor-pointer items-center"
      >
        <div className="col-span-12 md:col-span-3 flex items-center gap-3">
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={onToggleSelect}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 accent-[var(--ny-gold)]"
              data-testid={`select-driver-${stat.driver}`}
              title="Sélectionner"
            />
          )}
          <div className={`p-2 ${stat.explicit ? "bg-zinc-900" : "bg-zinc-500"} text-white`}>
            <Users size={18} weight="bold" />
          </div>
          <div>
            <div
              className="font-bold text-base uppercase tracking-tight flex items-center gap-2"
              style={{ fontFamily: "Chivo" }}
            >
              {stat.driver}
              {!stat.explicit && (
                <span className="text-[9px] bg-zinc-200 px-1.5 py-0.5 font-mono">
                  AUTO
                </span>
              )}
            </div>
            <div className="text-xs text-zinc-500 flex flex-wrap gap-x-3">
              {stat.phone && (
                <span className="inline-flex items-center gap-1 mono">
                  <Phone size={10} weight="bold" />
                  {stat.phone}
                </span>
              )}
              {stat.email && (
                <span className="inline-flex items-center gap-1">
                  <EnvelopeSimple size={10} weight="bold" />
                  {stat.email}
                </span>
              )}
              {!stat.phone && !stat.email && (
                <span>Dernier impact : {fmtDate(stat.last_date)}</span>
              )}
            </div>
          </div>
        </div>
        <div className="col-span-6 md:col-span-2">
          <div className="label-tag">Impacts</div>
          <div
            className="text-2xl font-black mono"
            style={{ fontFamily: "Chivo" }}
            data-testid={`driver-count-${stat.driver}`}
          >
            {stat.impact_count}
          </div>
        </div>
        <div className="col-span-6 md:col-span-2">
          <div className="label-tag">Coût total</div>
          <div className="text-lg font-bold mono">
            {stat.total_cost.toLocaleString()} €
          </div>
        </div>
        <div className="col-span-6 md:col-span-2">
          <div className="label-tag">Camions</div>
          <div className="text-lg font-bold mono">{stat.trucks.length}</div>
        </div>
        <div className="col-span-6 md:col-span-2 flex flex-wrap gap-1">
          {sev.critique > 0 && (
            <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest sev-critique">
              {sev.critique} critique
            </span>
          )}
          {sev.majeur > 0 && (
            <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest sev-majeur">
              {sev.majeur} majeur
            </span>
          )}
          {sev.mineur > 0 && (
            <span className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest sev-mineur">
              {sev.mineur} mineur
            </span>
          )}
        </div>
        <div className="col-span-12 md:col-span-1 flex justify-end items-center gap-1">
          {stat.explicit && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="p-1 hover:bg-zinc-100"
                data-testid={`edit-driver-${stat.driver}`}
              >
                <PencilSimple size={14} weight="bold" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-1 hover:bg-red-100"
                data-testid={`delete-driver-${stat.driver}`}
              >
                <Trash size={14} weight="bold" className="text-red-600" />
              </button>
            </>
          )}
          {expanded ? (
            <CaretDown size={18} weight="bold" />
          ) : (
            <CaretRight size={18} weight="bold" />
          )}
        </div>
      </div>
      {expanded && (
        <div className="border-t border-zinc-200 bg-zinc-50 p-4 space-y-3">
          {stat.notes && (
            <div>
              <div className="label-tag mb-1">Notes</div>
              <div className="text-sm whitespace-pre-wrap">{stat.notes}</div>
            </div>
          )}
          <div>
            <div className="label-tag mb-2">
              Camions touchés ({stat.trucks.length})
            </div>
            {stat.trucks.length === 0 ? (
              <div className="text-sm text-zinc-500">
                Aucun impact attribué à ce chauffeur.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {stat.trucks.map((t) => (
                  <Link
                    key={t.truck_id}
                    to={`/trucks/${t.truck_id}`}
                    className="bg-white border border-zinc-300 hover:border-zinc-900 px-3 py-2 flex items-center justify-between"
                    data-testid={`driver-truck-${t.truck_id}`}
                  >
                    <div>
                      <div className="mono font-bold uppercase text-sm">
                        {t.license_plate}
                      </div>
                      <div className="text-xs text-zinc-500 uppercase">
                        {t.van_type}
                      </div>
                    </div>
                    <ArrowSquareOut size={16} weight="bold" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DriversPage() {
  const { sites: allSitesList } = useSites();
  const { user } = useAuth();
  const sitesList = filterSitesForUser(allSitesList, user);
  const userScope = getUserAllowedSites(user);
  const defaultDriverTab =
    userScope && userScope.length === 1
      ? userScope[0]
      : sitesList[0]?.code || "ALL";
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [sortBy, setSortBy] = useState("count");
  const [siteTab, setSiteTab] = useState(defaultDriverTab); // DLP4 | DWB9 | ALL
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await fetchDriversStats();
      setStats(data);
    } catch (e) {
      if (!silent) toast.error("Erreur chargement chauffeurs");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);
  useAutoRefresh(() => load({ silent: true }), 20000);

  const removeDriver = async () => {
    if (!confirmDelete) return;
    try {
      await deleteDriver(confirmDelete.id);
      toast.success("Chauffeur supprimé");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error("Erreur suppression");
    }
  };

  const sorted = [...stats]
    .filter((s) => {
      if (siteTab !== "ALL") {
        const ds = s.site || "ALL";
        if (ds !== siteTab && ds !== "ALL") return false;
      }
      if (query && !s.driver.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "cost") return b.total_cost - a.total_cost;
      if (sortBy === "recent") {
        const ad = a.last_date || "";
        const bd = b.last_date || "";
        return bd.localeCompare(ad);
      }
      if (sortBy === "name") return a.driver.localeCompare(b.driver);
      return b.impact_count - a.impact_count;
    });

  const visibleSelectableIds = sorted.filter((s) => s.id).map((s) => s.id);
  const allVisibleSelected =
    visibleSelectableIds.length > 0 &&
    visibleSelectableIds.every((id) => selected.has(id));

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleSelectableIds.forEach((id) => next.delete(id));
      } else {
        visibleSelectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const res = await bulkDeleteDrivers(ids);
      toast.success(`${res.deleted} chauffeur(s) supprimé(s)`);
      clearSelection();
      setBulkConfirm(false);
      load();
    } catch {
      toast.error("Erreur suppression");
    }
  };

  const countsBySite = stats.reduce(
    (acc, s) => {
      const v = s.site || "ALL";
      acc[v] = (acc[v] || 0) + 1;
      acc.TOTAL += 1;
      return acc;
    },
    { ALL: 0, TOTAL: 0 }
  );

  const totalImpacts = stats.reduce((s, x) => s + x.impact_count, 0);
  const totalCost = stats.reduce((s, x) => s + x.total_cost, 0);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-8" data-testid="drivers-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:underline"
          data-testid="back-link"
        >
          <ArrowLeft size={16} weight="bold" /> Retour
        </Link>
      </div>

      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tag">NY Trans · Statistiques</div>
          <h1 className="serif-display text-5xl md:text-7xl mt-2 leading-none">
            Chauffeurs
          </h1>
          <div className="ny-divider w-40 mt-3" />
          <p className="text-zinc-700 mt-3 max-w-xl">
            Gérez la liste de vos chauffeurs et suivez les impacts qui leur sont
            attribués.
          </p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          className="bg-black text-white px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[var(--ny-gold-deep)] transition-none inline-flex items-center gap-2 brutalist-shadow-sm border border-black"
          data-testid="add-driver-btn"
        >
          <Plus size={16} weight="bold" /> Nouveau chauffeur
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-zinc-900 p-5 brutalist-shadow-sm">
          <div className="label-tag">Chauffeurs identifiés</div>
          <div
            className="text-4xl md:text-5xl font-black tracking-tighter mt-2"
            style={{ fontFamily: "Chivo" }}
            data-testid="stat-drivers-total"
          >
            {stats.length}
          </div>
        </div>
        <div className="bg-white border border-zinc-900 p-5 brutalist-shadow-sm">
          <div className="label-tag">Impacts totaux</div>
          <div
            className="text-4xl md:text-5xl font-black tracking-tighter mt-2"
            style={{ fontFamily: "Chivo" }}
          >
            {totalImpacts}
          </div>
        </div>
        <div className="bg-white border border-zinc-900 p-5 brutalist-shadow-sm">
          <div className="label-tag">Coût total</div>
          <div
            className="text-4xl md:text-5xl font-black tracking-tighter mt-2 mono"
            style={{ fontFamily: "Chivo", color: "#DC2626" }}
          >
            {totalCost.toLocaleString()} €
          </div>
        </div>
      </div>

      {/* Site tabs */}
      <div className="flex items-center gap-2 mb-2 border-b border-zinc-900 flex-wrap" data-testid="drivers-site-tabs">
        {[
          ...sitesList.map((s) => ({ key: s.code, label: s.name ? `${s.code} · ${s.name}` : s.code })),
          ...(sitesList.length > 1 ? [{ key: "ALL", label: "Tous" }] : []),
        ].map((s) => {
          const active = siteTab === s.key;
          const count = s.key === "ALL"
            ? countsBySite.TOTAL
            : (countsBySite[s.key] || 0) + countsBySite.ALL;
          return (
            <button
              key={s.key}
              onClick={() => {
                setSiteTab(s.key);
                clearSelection();
              }}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border border-b-0 inline-flex items-center gap-2 ${
                active
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
              }`}
              data-testid={`drivers-tab-${s.key}`}
            >
              {s.label}
              <span className={`mono px-1.5 py-0.5 text-[10px] ${active ? "bg-[var(--ny-gold)] text-zinc-900" : "bg-zinc-100 text-zinc-600"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="bg-zinc-900 text-white px-4 py-2 mb-1 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
          <span className="text-xs font-bold uppercase tracking-widest">
            {selected.size} sélectionné(s)
          </span>
          <button
            onClick={clearSelection}
            className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-white/50 hover:bg-white hover:text-black"
            data-testid="bulk-clear"
          >
            Désélectionner
          </button>
          <button
            onClick={() => setBulkConfirm(true)}
            className="ml-auto px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1.5"
            data-testid="bulk-delete-btn"
          >
            <Trash size={12} weight="bold" /> Supprimer la sélection
          </button>
        </div>
      )}

      {/* Search + Sort */}
      <div className="bg-white border border-zinc-900 mb-1 p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass
            size={16}
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
          />
          <input
            type="text"
            placeholder="Rechercher un chauffeur…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-zinc-300 bg-white focus:border-zinc-900 outline-none font-mono text-sm"
            data-testid="search-driver"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {[
            { v: "count", l: "Nb impacts" },
            { v: "cost", l: "Coût" },
            { v: "recent", l: "Récent" },
            { v: "name", l: "Nom A-Z" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setSortBy(s.v)}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border transition-none ${
                sortBy === s.v
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
              }`}
              data-testid={`sort-${s.v}`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-zinc-900 bg-zinc-100" data-testid="drivers-list">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-zinc-900 text-white items-center">
          <div className="col-span-12 md:col-span-3 label-tag !text-zinc-400 inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
              className="w-4 h-4 accent-[var(--ny-gold)]"
              data-testid="select-all-drivers"
              title="Tout sélectionner"
            />
            Chauffeur
          </div>
          <div className="col-span-6 md:col-span-2 label-tag !text-zinc-400">
            Impacts
          </div>
          <div className="col-span-6 md:col-span-2 label-tag !text-zinc-400">
            Coût total
          </div>
          <div className="col-span-6 md:col-span-2 label-tag !text-zinc-400">
            Camions
          </div>
          <div className="col-span-6 md:col-span-2 label-tag !text-zinc-400">
            Gravité
          </div>
          <div className="col-span-12 md:col-span-1"></div>
        </div>

        {loading && (
          <div className="p-8 text-center text-zinc-500" data-testid="loading">
            Chargement des statistiques…
          </div>
        )}
        {!loading && sorted.length === 0 && (
          <div className="p-12 text-center bg-white" data-testid="empty-state">
            <Users size={48} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
            <div className="text-lg font-bold uppercase">Aucun chauffeur</div>
            <div className="text-zinc-500 mt-1">
              Ajoutez un chauffeur ou créez un impact pour le voir apparaître.
            </div>
          </div>
        )}
        {!loading &&
          sorted.map((s) => (
            <DriverCard
              key={s.driver}
              stat={s}
              expanded={expandedId === s.driver}
              onToggle={() =>
                setExpandedId((cur) => (cur === s.driver ? null : s.driver))
              }
              onEdit={() => {
                setEditing(s);
                setDialogOpen(true);
              }}
              onDelete={() => setConfirmDelete(s)}
              selected={s.id ? selected.has(s.id) : false}
              onToggleSelect={s.id ? () => toggleSelect(s.id) : null}
            />
          ))}
      </div>

      <DriverDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        editing={editing}
        onSaved={load}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="rounded-none border border-zinc-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce chauffeur ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le chauffeur <strong>{confirmDelete?.driver}</strong> sera retiré de la
              liste. Les impacts déjà attribués à ce nom resteront enregistrés
              (vous pourrez les modifier individuellement).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-900">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={removeDriver}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-driver"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent className="rounded-none border border-zinc-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selected.size} chauffeur(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les impacts déjà attribués à ces noms resteront enregistrés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-900">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="confirm-bulk-delete"
            >
              Supprimer {selected.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
